import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChecklistFieldDef, ChecklistItemDto, ChecklistValues, ContactPickItem, DocumentDto, UserDto } from '@tww/shared';
import { ROLE_NAMES, checklistFolder, fmtDate, itemState, todayIso } from '@tww/shared';
import { api, ApiRequestError, qs } from '../../api/client';
import { ContactPicker } from '../../ui/ContactPicker';
import { Modal } from '../../ui/Modal';
import { Note } from '../../ui/basics';
import { ChecksWithDateInput, DateInput, DateListInput, Field, NumberInput, ReminderField, Select, TextArea, TextInput } from '../../ui/fields';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/Toast';
import { useLookup } from '../../api/hooks';
import { ROLE_SHORT } from './common';

/**
 * Aufgabenmaske (Plan 04 Abschnitt 5.5, Prototyp Screen 04): Felder nach Vorlage, Frist mit „Standard wiederherstellen“,
 * Zuständige Person, Dateien per Drop-Zone. Modal, weil die Punkte kurz sind; einspaltig.
 */
export function ChecklistItemModal({ item, groupName, groupRole, actionDate, region, onClose, onSaved }: { item: ChecklistItemDto; groupName: string; groupRole: string | null; actionDate: string | null; region: string | null; onClose: () => void; onSaved: (it: ChecklistItemDto) => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [values, setValues] = useState<ChecklistValues>({ ...item.values, erinnerung: { date: item.reminder_date, note: item.reminder_note ?? '' } });
  const [due, setDue] = useState<string>(item.due_date ?? '');
  const [dueDefault, setDueDefault] = useState(item.due_is_default);
  const [userId, setUserId] = useState<number | null>(item.responsible_user_id);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [contactsByField, setContactsByField] = useState<Record<string, ContactPickItem[]>>(() =>
    Object.fromEntries(Object.entries(item.contacts).map(([k, list]) => [k, list.map((c) => ({ id: c.id, display_name: c.display_name, sub: c.sub, rating_grade: c.rating_grade, rating_reason: null, city: null, region: null, last_price: null, categories: [] }))])),
  );
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<{ items: Pick<UserDto, 'id' | 'display_name' | 'initials'>[] }>('/users/pick').then((r) => r.items), staleTime: 60_000 });
  const kinds = useLookup('document_kinds');
  /** Dokumentart aus dem Punkt ableiten („Rechnungen“ → Rechnung, „Pflanzplan vom Förster“ → Pflanzplan, Angebotsfelder → Angebot) */
  const uploadKind = useMemo(() => {
    const t = item.title.toLowerCase();
    const hit = kinds.values.find((k) => k.code !== 'foto' && k.code !== 'sonstiges' && t.includes(k.label.toLowerCase()));
    if (hit) return hit.code;
    return item.fields.some((f) => f.key === 'angebot' && f.file) ? 'angebot' : '';
  }, [item.title, item.fields, kinds.values]);
  const folder = checklistFolder(item.title);
  // Dateien des Punkts: normale Dateien der Aktion im Ordner des Punkts oder per ID verknüpft (auch nach Verschieben)
  const actionDocs = useQuery({ queryKey: ['documents', 'action', item.action_id], queryFn: () => api.get<{ items: DocumentDto[] }>(`/documents${qs({ entity_type: 'action', entity_id: item.action_id })}`).then((r) => r.items) });
  const linkedIds = useMemo(() => new Set(item.fields.flatMap((f) => [values[f.key], values[`${f.key}_files`]]).flatMap((v) => (Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : []))), [item.fields, values]);
  const docs = { data: (actionDocs.data ?? []).filter((d) => linkedIds.has(d.id) || d.folder === folder || d.folder.startsWith(`${folder}/`)) };
  const preview = useMemo(() => itemState(item, values), [item, values]);
  const set = (k: string, v: unknown) => setValues((s) => ({ ...s, [k]: v }));

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { values, responsible_user_id: userId, updated_at: item.updated_at };
      if (dueDefault && !item.due_is_default) body.reset_due = true;
      else if (!dueDefault && (due || '') !== (item.due_date ?? '')) body.due_date = due || null;
      return api.patch<ChecklistItemDto>(`/actions/${item.action_id}/checklist/${item.id}`, body);
    },
    onSuccess: (it) => {
      qc.invalidateQueries({ queryKey: ['action', item.action_id] });
      qc.invalidateQueries({ queryKey: ['actions'] });
      qc.invalidateQueries({ queryKey: ['today'] });
      toast(it.state === 'done' ? 'Punkt erledigt' : 'Gespeichert');
      onSaved(it);
    },
    onError: (e: Error) => {
      if (e instanceof ApiRequestError && e.fields) setErrors(e.fields);
      toast(e.message, 'error');
    },
  });
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const out: DocumentDto[] = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append('entity_type', 'action');
        fd.append('entity_id', String(item.action_id));
        fd.append('folder', folder);
        if (uploadKind) fd.append('kind', uploadKind);
        fd.append('file', f);
        out.push(await api.upload<DocumentDto>('/documents', fd));
      }
      return out;
    },
    onSuccess: (list) => {
      qc.invalidateQueries({ queryKey: ['documents', 'action', item.action_id] });
      qc.invalidateQueries({ queryKey: ['folders', 'action', item.action_id] });
      // Datei-IDs in das erste Dateifeld übernehmen
      const ff = item.fields.find((f) => f.type === 'file' || f.file);
      if (ff) {
        const key = ff.type === 'file' ? ff.key : `${ff.key}_files`;
        set(key, [...((values[key] as number[] | undefined) ?? []), ...list.map((d) => d.id)]);
      }
      toast(`${list.length} Datei${list.length > 1 ? 'en' : ''} hochgeladen`);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  useEffect(() => {
    if (!dueDefault) return;
    setDue(item.default_due ?? '');
  }, [dueDefault, item.default_due]);

  const fileFields = item.fields.filter((f) => f.type === 'file');
  const hasFiles = fileFields.length > 0 || item.fields.some((f) => f.file);
  const roleLabel = item.responsible_user_name ?? (item.responsible_role ? ROLE_SHORT[item.responsible_role] : groupRole ? ROLE_SHORT[groupRole] ?? groupRole : '–');
  return (
    <Modal
      title={item.title}
      subtitle={roleLabel && roleLabel !== groupName ? `${groupName} · zuständig ${roleLabel}` : groupName}
      onClose={onClose}
      size="default"
      footer={
        <>
          {preview !== item.state ? (
            <span className={`item-change small ${preview === 'done' ? 'ok' : 'mut'}`}>
              {preview === 'done' ? <><Icon name="check" /> Wird als erledigt markiert</> : preview === 'not_needed' ? 'Wird als nicht benötigt markiert' : 'Wird wieder offen'}
            </span>
          ) : (
            <span className="item-change" />
          )}
          <button className="btn" type="button" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" type="button" onClick={() => save.mutate()} disabled={save.isPending || !item.can_edit}>
            Speichern
          </button>
        </>
      }
    >
      {!item.can_edit && <Note kind="info">Nur lesbar, zuständig: {groupRole ? ROLE_NAMES[groupRole as keyof typeof ROLE_NAMES] ?? groupRole : 'die Aktionsleitung'}.</Note>}
      <div className="form one">
        {item.fields.map((f) => (
          <ChecklistField key={f.key} f={f} value={values[f.key]} onChange={(v) => set(f.key, v)} region={region} disabled={!item.can_edit} contacts={contactsByField[f.key] ?? []} onContacts={(list) => setContactsByField((s) => ({ ...s, [f.key]: list }))} error={errors[`values.${f.key}`]} />
        ))}
        <div className="form full">
          <Field label="Frist" hint={dueDefault ? (item.default_due ? 'Standardfrist' : actionDate ? undefined : 'ohne Aktionstag keine Standardfrist') : undefined}>
            <div className="frist-row">
              <input type="date" value={due} disabled={!item.can_edit} onChange={(e) => (setDue(e.target.value), setDueDefault(false))} aria-label="Frist" />
              {!dueDefault && item.can_edit && item.default_due && (
                <button type="button" className="btn sm" onClick={() => setDueDefault(true)} title="Eigene Fristen wandern bei Terminänderungen der Aktion nicht mit">
                  Standard {fmtDate(item.default_due).slice(0, 6)}
                </button>
              )}
            </div>
          </Field>
          <Select label="Zuständige Person" value={userId ? String(userId) : ''} onChange={(v) => setUserId(v ? Number(v) : null)} options={(users.data ?? []).map((u) => ({ value: String(u.id), label: u.display_name }))} placeholder={groupRole ? `Team ${ROLE_SHORT[groupRole] ?? groupRole}` : 'niemand persönlich'} disabled={!item.can_edit} />
        </div>
        {hasFiles && (
          <Field label={<>Dateien <span className="check-count">im Ordner „{folder}“ der Aktion</span></>} full>
            {(docs.data ?? []).length > 0 && (
              <ul className="docs">
                {(docs.data ?? []).map((d) => (
                  <li key={d.id}>
                    <Icon name="doc" />
                    <span className="nm">
                      <a className="link" href={`/api/v1/documents/${d.id}/file`} target="_blank" rel="noreferrer">
                        {d.title || d.original_name}
                      </a>
                      <span>{fmtDate(d.created_at)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {item.can_edit && (
              <label className="dropzone" onDragOver={(e) => e.preventDefault()} onDrop={(e) => (e.preventDefault(), upload.mutate(Array.from(e.dataTransfer.files)))}>
                <Icon name="upload" /> Dateien hierher ziehen oder klicken
                <input type="file" multiple hidden onChange={(e) => e.target.files && upload.mutate(Array.from(e.target.files))} />
              </label>
            )}
          </Field>
        )}
      </div>
    </Modal>
  );
}

/** Ein Feld der Aufgabenmaske nach Typ (Plan 04 A-12) */
export function ChecklistField({ f, value, onChange, region, disabled, contacts, onContacts, error }: { f: ChecklistFieldDef; value: unknown; onChange: (v: unknown) => void; region: string | null; disabled?: boolean; contacts: ContactPickItem[]; onContacts: (l: ContactPickItem[]) => void; error?: string }) {
  switch (f.type) {
    case 'select':
      return <Select label={f.label} value={(value as string) ?? ''} onChange={onChange} options={(f.options ?? []).map((o) => ({ value: o, label: o }))} placeholder="wählen" disabled={disabled} error={error} />;
    case 'date':
      return <DateInput label={f.label} value={(value as string) ?? ''} onChange={onChange} disabled={disabled} error={error} />;
    case 'dates':
      return <DateListInput label={f.label} value={(value as string[]) ?? []} onChange={onChange} disabled={disabled} error={error} />;
    case 'number':
      return <NumberInput label={f.label} value={(value as number) ?? null} onChange={onChange} disabled={disabled} error={error} />;
    case 'textarea':
      return <TextArea label={f.label} value={(value as string) ?? ''} onChange={onChange} disabled={disabled} error={error} />;
    case 'text':
      return <TextInput label={f.label} value={(value as string) ?? ''} onChange={onChange} disabled={disabled} error={error} />;
    case 'checks':
      return <ChecksWithDateInput label={f.label} value={(value as Record<string, string | true>) ?? {}} onChange={onChange} options={f.options ?? []} noDate={f.nodate} disabled={disabled} error={error} />;
    case 'reminder': {
      const r = (value as { date: string | null; note: string }) ?? { date: null, note: '' };
      return <ReminderField label={f.label} value={{ date: r.date ?? null, note: r.note ?? '' }} onChange={onChange} disabled={disabled} error={error} />;
    }
    case 'file':
      return (
        <Field label={f.label}>
          <span className="mut small">{Array.isArray(value) && value.length ? `${value.length} Datei${value.length > 1 ? 'en' : ''} verknüpft` : 'noch keine Datei'}</span>
        </Field>
      );
    case 'contact': {
      const ids = (value as number[] | undefined) ?? [];
      return (
        <Field label={f.label} error={error}>
          {f.multi ? (
            <>
              {contacts.length > 0 && (
                <ul className="docs" style={{ marginBottom: 6 }}>
                  {contacts.map((c) => (
                    <li key={c.id}>
                      <span className="nm">{c.display_name}{c.sub && <span>{c.sub}</span>}</span>
                      {!disabled && (
                        <button type="button" className="icon-btn ext" aria-label="Entfernen" onClick={() => (onChange(ids.filter((x) => x !== c.id)), onContacts(contacts.filter((x) => x.id !== c.id)))}>
                          <Icon name="x" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {!disabled && (
                <ContactPicker
                  value={null}
                  category={f.contact_category}
                  region={region ?? undefined}
                  placeholder="Kontakt hinzufügen …"
                  onChange={(c) => {
                    if (!c || ids.includes(c.id)) return;
                    onChange([...ids, c.id]);
                    onContacts([...contacts, c]);
                  }}
                />
              )}
            </>
          ) : disabled ? (
            <span>{contacts[0]?.display_name ?? <span className="mut">–</span>}</span>
          ) : (
            <ContactPicker value={contacts[0] ?? null} category={f.contact_category} region={region ?? undefined} onChange={(c) => (onChange(c ? [c.id] : []), onContacts(c ? [c] : []))} />
          )}
        </Field>
      );
    }
    default:
      return null;
  }
}

export const todayForField = todayIso;
